import { Key } from 'react'

import produce from 'immer'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import isUndefined from 'lodash/isUndefined'

import { date, type Dayjs } from 'configuration/dayjs'
import { isField } from 'helpers/typeGuard'
import {
    ChildField,
    CurrencyCode,
    DateRangeFilter,
    Field,
    Pagination,
    PeriodDeltaType,
    Sorter,
} from 'types'

import { getPriorPeriodDateRange, presetRanges } from './dateRange'
import { formatCerebroDate } from './formatting'

const idNameMapping: { [id: string]: string } = {
    keyword__id: 'keyword__text',
    ad_group__id: 'ad_group__name',
    campaign__id: 'campaign__name',
    portfolio__id: 'portfolio__name',
    profile__id: 'profile__brand_name',
    advertiser__id: 'advertiser__name',
    order__id: 'order__name',
    line_item__id: 'line_item__name',
    metadata_id: 'metadata__title',
    campaign__label_id: 'campaign__label_name',
    keyword__label_id: 'keyword__label_name',
    porfolio__label_id: 'porfolio__label_name',
    product__label_id: 'product__label_name',
    profile__label_id: 'profile__label_name',
    order__label_id: 'order__label_name',
    line_item__label_id: 'line_item__label_name',
    // this is for sov management endpoint
    id: 'text',
}

const getMetricNameFromField = <T>(field: Field<T> | ChildField<T>): string => {
    if (isString(field.dataIndex)) {
        return field.dataIndex
    }
    if (isArray(field.dataIndex)) {
        return String(field.dataIndex.slice(-1))
    }
    if (isField(field)) {
        return field.id
    }
    throw new Error(`Unrecognized format for field ${JSON.stringify(field)}`)
}

const getCerebroFormattedField = <T>(
    field: Field<T> | ChildField<T>
): string => {
    if (isString(field.dataIndex)) {
        return field.dataIndex
    }
    if (isArray(field.dataIndex)) {
        return field.dataIndex.join('__')
    }
    if (isField(field)) {
        return field.id
    }
    throw new Error(`Unrecognized format for field ${JSON.stringify(field)}`)
}

type SorterParams = Sorter & { filterPositiveValues?: boolean }

interface SorterFormatted {
    [key: string]: string | number
}

export interface MetricsFormatted {
    metrics?: string
}

export interface ColumnsFormatted {
    columns?: string
}

interface PaginationFormatted {
    limit?: number
    offset?: number
}

interface CurrencyFormatted {
    currency?: CurrencyCode
}

export interface SignupData {
    name: string
    token?: string
    phone?: string
    email: string
    password: string
    terms_of_service: boolean
}

export interface SignupDataFormatted {
    username: string
    token?: string
    password: string
    attributes: {
        email: string
        name: string
        'custom:terms_of_service': '1' | '0'
        phone_number?: string
    }
}

/**
 * Convert sorter into query params using Cerebro's ordering conventions
 */
export const formatSorter = (sorter?: SorterParams): SorterFormatted => {
    const { field, order, filterPositiveValues } = sorter ?? {}
    const params: SorterFormatted = {}

    if (!field || !order) {
        return params
    }

    let formattedField =
        typeof field === 'string' || typeof field === 'number'
            ? `${field}`.replace(/\./g, '__')
            : (field as Key[]).join('__')

    // replace xx__id with xx__name in "order by"
    if (formattedField in idNameMapping) {
        formattedField = idNameMapping[formattedField]
    }

    return produce(params, (draft) => {
        // set the ordering according to Cerebro conventions
        draft.ordering =
            order === 'descend' ? `-${formattedField}` : formattedField

        // Filter only positive values if `filterPositiveValues` is true
        if (filterPositiveValues) {
            draft[`${formattedField}__gt`] = 0
        }
    })
}

/**
 * Convert array of metric names into query params using Cerebro's
 * metrics parameter conventions
 */
export const formatStringMetrics = (metrics: string[]): MetricsFormatted =>
    isEmpty(metrics) ? {} : { metrics: metrics.join() }

/**
 * Convert metrics into query params using Cerebro's metrics parameter conventions
 * Adds total and period delta metrics if showTotalRow and showPeriodDeltas are true
 */
export const formatMetrics = <T>(
    metrics: Field<T>[],
    showPeriodDeltas: boolean,
    showTotalRow?: boolean,
    fieldsToIgnore?: string[]
): MetricsFormatted => {
    if (isEmpty(metrics)) {
        return {}
    }

    return {
        metrics: metrics
            .reduce((accumulator: string[], metric: Field<T>): string[] => {
                const metricName = getMetricNameFromField(metric)
                accumulator.push(metricName)

                if (showTotalRow && metric.isTotalSupported) {
                    accumulator.push(`${metricName}__total`)

                    if (showPeriodDeltas) {
                        metric.childrenFields?.forEach((child) => {
                            const splitName = child.dataIndex
                                ?.slice(-1)[0]
                                .split('__')
                            if (!splitName) {
                                return
                            }

                            const [periodName, ...mainName] = [
                                splitName.pop(),
                                ...splitName,
                            ]
                            accumulator.push(
                                `${mainName.join('__')}__total__${periodName}`
                            )
                        })
                    }
                }

                if (showPeriodDeltas) {
                    metric.childrenFields?.forEach((child) =>
                        accumulator.push(getMetricNameFromField(child))
                    )
                }

                return accumulator
            }, [])
            .filter((metric) => !fieldsToIgnore?.includes(metric))
            .join(','),
    }
}

/**
 * Converts pagination into pagination query parameters according to Cerebro's conventions
 */
export const formatPagination = (
    pagination?: Pagination
): PaginationFormatted =>
    pagination?.pageSize && pagination?.current
        ? {
              limit: pagination.pageSize,
              offset: (pagination.current - 1) * pagination.pageSize,
          }
        : {}

/**
 * Formats currency codes into query parameters according to Cerebro's conventions
 */
export const formatCurrency = (currency?: CurrencyCode): CurrencyFormatted =>
    currency ? { currency } : {}

/**
 * Formats Fields into "columns" query parameters according to Cerebro's conventions
 */
export const formatColumns = <T>(
    columns: Field<T>[],
    showPeriodDeltas: boolean
): ColumnsFormatted => {
    if (isEmpty(columns)) {
        return {}
    }

    const uniqueColumnNames: Set<string> = new Set()

    columns.forEach((col: Field<T>) => {
        if (!isUndefined(col.dataIndex)) {
            uniqueColumnNames.add(getCerebroFormattedField(col))
            if (showPeriodDeltas && !isEmpty(col.childrenFields)) {
                col.childrenFields?.forEach((child) => {
                    if (child.metricOptions?.isDelta) {
                        uniqueColumnNames.add(getCerebroFormattedField(child))
                    }
                })
            }
        }
    })

    // add resource name to columns if id is in the columns
    const nameColumns: Set<string> = new Set()
    uniqueColumnNames.forEach((columnName) => {
        if (columnName in idNameMapping) {
            nameColumns.add(idNameMapping[columnName])
        }
    })

    // Add name columns to the final list first, ex. campaign__name early is more readable
    const finalColumnNames: Set<string> = new Set([...nameColumns])

    uniqueColumnNames.forEach((columnName) => {
        finalColumnNames.add(columnName)
    })

    return { columns: [...finalColumnNames].join(',') }
}

/**
 * Format sign up form values for Cognito API
 */
export const formatSignUpData = ({
    name,
    token,
    phone,
    email,
    password,
    terms_of_service,
}: SignupData): SignupDataFormatted => {
    let phoneFormatted
    if (phone) {
        const phoneParsed = parsePhoneNumberFromString(phone, 'US')
        if (phoneParsed && phoneParsed.isValid()) {
            phoneFormatted = phoneParsed.format('E.164')
        }
    }
    return {
        username: email, // using email as username, per Cognito settings
        token,
        password,
        attributes: {
            email,
            name,
            'custom:terms_of_service': terms_of_service
                ? ('1' as const)
                : ('0' as const),
            ...(phoneFormatted ? { phone_number: phoneFormatted } : {}),
        },
    }
}

type DateParams = {
    report_date_min?: string
    report_date_max?: string
}

export function formatReportDateRangeParams(
    dates: DateRangeFilter | undefined,
    rangeLag?: number,
    fiscalYearStartMonth?: number
): DateParams {
    const dateParams: DateParams = {}
    if (dates) {
        if (Array.isArray(dates)) {
            dateParams.report_date_min = formatCerebroDate(dates[0])
            dateParams.report_date_max = formatCerebroDate(dates[1])
        } else {
            const { range } = presetRanges(
                date(),
                rangeLag,
                fiscalYearStartMonth
            )[dates]
            dateParams.report_date_min = formatCerebroDate(range[0])
            dateParams.report_date_max = formatCerebroDate(range[1])
        }
    }
    return dateParams
}

type PeriodDeltaParams<T extends string> = {
    [K in `previous_period_${T}_min` | `previous_period_${T}_max`]: string
}

function generatePreviousPeriodParams(
    datePrefix: string,
    min: string,
    max: string
): PeriodDeltaParams<string> {
    return {
        [`previous_period_${datePrefix}_min`]: min,
        [`previous_period_${datePrefix}_max`]: max,
    }
}

export function formatPeriodDeltaDateRange<T extends string>(
    showPeriodDeltas: boolean,
    periodDeltaType: PeriodDeltaType | undefined,
    periodDeltaDateRange: string[],
    dateRangeFilter: DateRangeFilter,
    datePrefix: T = 'report_date' as T,
    rangeLag = 0,
    fiscalYearStartMonth?: number
): Partial<PeriodDeltaParams<T>> {
    if (showPeriodDeltas) {
        if (periodDeltaType === 'custom') {
            return generatePreviousPeriodParams(
                datePrefix,
                periodDeltaDateRange[0],
                periodDeltaDateRange[1]
            )
        }
        const range: Dayjs[] = isArray(dateRangeFilter)
            ? [date(dateRangeFilter[0]), date(dateRangeFilter[1])]
            : presetRanges(date(), rangeLag, fiscalYearStartMonth)[
                  dateRangeFilter
              ].range

        const priorRange = getPriorPeriodDateRange(
            range[0],
            range[1],
            periodDeltaType,
            dateRangeFilter,
            false,
            fiscalYearStartMonth
        )

        return generatePreviousPeriodParams(
            datePrefix,
            priorRange[0],
            priorRange[1]
        )
    }

    return {}
}
